/**
 * PictureInput.js
 * Talkroute
 *
 * Created by Vel Genov on 10/23/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import { Icon, Dimmer, Loader } from "semantic-ui-react";
import CloseIcon from "@resources/icons/cross-small.svg"
import { clearFiles } from "../../actions/textMessageDraftActions";
import validateArray from "../../helpers/arrayValidator";

interface IPictureInputProps {
  pictures: string[];
  isLoading: boolean;
  clearFiles: () => any;
}

/**
 * A react component that displays a draft picture attachment input
 */
class PictureInput extends React.Component<IPictureInputProps, any> {
  static defaultProps = {
    pictures: [],
    isLoading: false,
  };

  /**
   * Displays the attachment placeholder
   *
   * @param picture a path to the picture
   * @param isLoading indicates wheather this attachment is currently loading
   */
  showPicturePlaceholder = (picture, isLoading) => {
    return (
      <div
        key={picture}
        onClick={this.removePicture}
        onKeyPress={() => {}}
        role="button"
        tabIndex={0}
        style={{ display: "inline", position: "relative", outline: "none" }}
      >
        {isLoading ? this.showLoadingScreen() : this.showPicture(picture)}
      </div>
    );
  };

  /**
   * Shows the actual picture
   *
   * @param picture a path to the picture
   */
  showPicture = (picture) => (
    <div>
      <CloseIcon
        style={{
          position: "absolute",
          right: "-7px",
          top: "-7px",
          margin: "0px",
          padding: "0px",
          width: 9,
          height: 9,
          cursor: "pointer"
        }}
      />
      <img
        alt="attachment"
        src={picture}
        style={{
          maxWidth: "200px",
          maxHeight: "100px",
          borderRadius: "10px",
        }}
      />
    </div>
  );

  /**
   * Shows a loading dimmer
   */
  showLoadingScreen = () => (
    <div
      style={{
        width: "200px",
        height: "100px",
      }}
    >
      <Dimmer
        active
        style={{
          borderRadius: "10px",
        }}
      >
        <Loader inverted />
      </Dimmer>
    </div>
  );

  /**
   * Process the array of picture attachments
   *
   * @param pictures an array of picture attachments to process
   * @param isLoading indicates wheather attachments are being loaded right now
   */
  processPictures = () => {
    const { pictures, isLoading } = this.props;

    if (validateArray(pictures)) {
      const processedPictures = pictures.map((picture) =>
        this.showPicturePlaceholder(picture, isLoading)
      );

      return processedPictures;
    }

    return this.showPicturePlaceholder(pictures, isLoading);
  };

  /**
   * Remove a picture attachment
   */
  removePicture = () => {
    const { clearFiles: clearFilesLocal } = this.props;

    clearFilesLocal();
  };

  render() {
    return this.processPictures();
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = () => ({});

export default connect(mapStateToProps, { clearFiles })(PictureInput);
